/**
 * Where to go back to after the sign-in round trip.
 *
 * <p>A deep link into the workbench - a case shared by a colleague, `/workbench#/case/<id>` - lost
 * its destination at the sign-in screen: the OIDC redirect returns to `/callback`, which sent
 * everyone to their home page (walkthrough F9). The path is remembered in the tab's session
 * storage before the redirect and consumed once after it, the same lifetime the tokens have.
 *
 * <p>Pure over an injectable store so it can be tested without a DOM; `window.sessionStorage` is
 * the store in the browser. Best-effort like the tenant slug: storage can throw (Safari in
 * private mode), and a link that merely fails to be remembered must still sign in.
 */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const STORAGE_KEY = 'thesis3.returnPath';

/** The paths that are not destinations: the signpost, the callback itself, and anything absolute. */
export function isReturnable(path: string): boolean {
  if (!path.startsWith('/') || path.startsWith('//')) return false;
  const bare = path.split(/[?#]/)[0];
  return bare !== '/' && bare !== '/callback';
}

/** Remember `path` (pathname + search + hash) for after sign-in; a non-destination is forgotten. */
export function rememberReturnPath(path: string, store: KeyValueStore): void {
  try {
    if (isReturnable(path)) store.setItem(STORAGE_KEY, path);
    else store.removeItem(STORAGE_KEY);
  } catch {
    // Not remembered. Sign-in still works; only the landing differs.
  }
}

/** The remembered path, once: read and cleared together so a later sign-in does not replay it. */
export function consumeReturnPath(store: KeyValueStore): string | null {
  try {
    const stored = store.getItem(STORAGE_KEY);
    store.removeItem(STORAGE_KEY);
    return stored != null && isReturnable(stored) ? stored : null;
  } catch {
    return null;
  }
}
